import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider
} from '@stripe/react-stripe-js';
import { useAuth0User } from '@hooks/useAuth0User';
import { buildLoginHref } from '@lib/auth0/login-url';
import AnimateSpinner from '@components/animate-spinner';
import { useAccessToken } from '@hooks/useAccessToken';
import {
  generateEventId,
  getExistingPlanPath,
  getExternalUserId
} from '@lib/utils';
import {
  getFacebookBrowserId,
  getFacebookClickId,
  trackingPurchasePixelIDKey
} from '@lib/fpixel';
import FunnelHeader from '@components/quiz/shared/FunnelHeader';
import { createEmbeddedCheckoutSession } from '@api-service/checkout';
import { resolveCheckoutReturnPath } from '@lib/checkout/return-paths';
import {
  ARM_COUPON_QUERY_PARAM,
  resolveCheckoutCoupon
} from '@lib/checkout/arm-coupon';
import { readInitiatingPageUrl } from '@lib/checkout/initiating-page';
import { readFirstLandingPage } from '@lib/attribution/first-landing-page';
import { fireAddToCart } from '@lib/analytics/fireAddToCart';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import Button from '@components/atoms/button';
import { H2 } from '@components/atoms/typography';
import { resolveReferralId } from '@lib/rewardful';
import { getStripe } from '@lib/stripe';
import {
  readUtmCookie,
  toBareUtm,
  UTM_FIRST_TOUCH_COOKIE,
  UTM_LAST_TOUCH_COOKIE
} from '@lib/attribution/cookies';
import { ConsentManager } from '@lib/consent';
import { datadogRumService } from '@lib/datadogRum';

/**
 * FFH-1558 telemetry reasons. `missing-access-token` is "the visitor is not
 * logged in"; `checkout-session-failed` is "the Stripe session call failed"
 * - previously both collapsed into one generic error screen and only the
 * latter left any console trace.
 */
const CHECKOUT_ERROR_REASON = {
  missingAccessToken: 'missing-access-token',
  sessionFailed: 'checkout-session-failed',
  // FFH-2592: not an error condition. The API guard stopped a member who
  // already pays from buying a second subscription.
  alreadySubscribed: 'already-subscribed'
} as const;

const CHECKOUT_ENDPOINT = '/billing/checkout';

/** The API's typed 409 HAS_LIVE_SUBSCRIPTION refusal (FFH-2591). */
const HAS_LIVE_SUBSCRIPTION_STATUS = 409;
const HAS_LIVE_SUBSCRIPTION_CODE = 'HAS_LIVE_SUBSCRIPTION';

/**
 * A malformed or repeated planId query param makes Number() produce NaN,
 * which serialises to null in Datadog and breaks grouping; omit it instead.
 */
const parsePlanIdForTelemetry = (planIdValue: unknown): number | undefined => {
  const planIdNumber = Number(planIdValue);
  return Number.isFinite(planIdNumber) && planIdNumber > 0
    ? planIdNumber
    : undefined;
};

const CheckoutPage = () => {
  const { accessToken, accessTokenIsFetching } = useAccessToken();
  const router = useRouter();
  const { user, isLoading } = useAuth0User();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  // loadStripe is memoised inside getStripe(); useMemo keeps the same
  // promise across re-renders so the embedded provider does not remount.
  const stripePromise = useMemo(() => getStripe(), []);

  // FFH-991: Add to Cart fires when the checkout page is reached. fireAddToCart
  // is idempotent per session (safe across re-renders); the GTM pixel (off the
  // `add_to_cart` dataLayer push) and the server CAPI dedup on one event_id.
  useEffect(() => {
    // router.query.planId is string | string[] | undefined; guard for a single
    // string that parses to a valid positive plan id before firing, so an
    // array / malformed value never sends an AddToCart with planId NaN.
    const planIdParam = router.query.planId;
    if (isLoading || typeof planIdParam !== 'string') return;
    const planId = Number(planIdParam);
    if (!Number.isFinite(planId) || planId <= 0) return;
    fireAddToCart({
      planId,
      email: user?.email,
      externalId: getExternalUserId(user)?.toString()
    });
  }, [isLoading, router.query.planId, user]);

  useEffect(() => {
    if (
      isLoading ||
      accessTokenIsFetching ||
      !router.query.planId ||
      clientSecret
    ) {
      return;
    }

    const { planId } = router.query;
    const planIdNumber = Number(planId);

    // FFH-1743: the visitor's own coupon outranks the funnel arm's default, so
    // an affiliate or creator link still wins on a discount-arm funnel. See
    // resolveCheckoutCoupon for the full precedence.
    const coupon = resolveCheckoutCoupon({
      queryCoupon: router.query.coupon,
      storedCoupon: sessionStorage.getItem('coupon'),
      armCoupon: router.query[ARM_COUPON_QUERY_PARAM]
    });

    // FFH-1742: the trial arm's paywall appends trialDays to this page's URL
    // (inside the Auth0 returnTo). Guarded like planId: a repeated or
    // malformed param must not send NaN. The API is authoritative on the
    // value (clamps the range, excludes Ultra), so this only shapes the
    // request, never the entitlement.
    const trialDaysParam = router.query.trialDays;
    const trialDaysNumber =
      typeof trialDaysParam === 'string' ? Number(trialDaysParam) : Number.NaN;
    const trialPeriodDays =
      Number.isInteger(trialDaysNumber) && trialDaysNumber > 0
        ? trialDaysNumber
        : undefined;

    fetchClientSecret({ coupon, planIdNumber, trialPeriodDays });
  }, [
    accessToken,
    user,
    accessTokenIsFetching,
    isLoading,
    router.query.planId,
    clientSecret
  ]);

  const fetchClientSecret = async ({
    coupon,
    planIdNumber,
    trialPeriodDays
  }) => {
    if (!accessToken) return;

    try {
      const externalId = getExternalUserId(user);
      const fbClickId = getFacebookClickId();
      const purchasePixelEventId = generateEventId('purchase');
      const fbpCookie = getFacebookBrowserId(document);
      localStorage.setItem(trackingPurchasePixelIDKey, purchasePixelEventId);
      // FFH-1991: read the persistent Rewardful referral (survives a
      // click-now-pay-later return session) rather than the sessionStorage
      // snapshot alone.
      const referralId = resolveReferralId();

      // FFH-1160 attribution payload. UTMs come from worker-shaped
      // JSON cookies (or the FE fallback writer); remapped to bare
      // keys to match the backend `utmDict` schema.
      const utmFirstTouch = toBareUtm(readUtmCookie(UTM_FIRST_TOUCH_COOKIE));
      const utmLastTouch = toBareUtm(readUtmCookie(UTM_LAST_TOUCH_COOKIE));
      // The checkout-INITIATING page — recorded by useRecordInitiatingPage in
      // _app. Deliberately NO fallback to this page's own URL: that fallback
      // was writing /checkout-stripe into converted_page_url whenever nothing
      // had been recorded (direct landing, or sessionStorage unavailable in an
      // in-app browser), which is the one value the field must never hold.
      // Omitting it is honest — the backend and Stripe metadata both treat
      // sourceUrl as optional.
      const sourceUrl = readInitiatingPageUrl();
      // FFH-1849: the write-once first-landing-page cookie, carried through to
      // subscription metadata + the ChartMogul mirror for paywall attribution.
      const firstLandingPage = readFirstLandingPage();
      // FFH-970: send the structured 3-axis consent (the backend treats it as
      // authoritative over the legacy single-string consentState and forwards
      // it to each platform's processing-options on the Purchase CAPI). Omit
      // entirely when CookieYes hasn't recorded a decision — distinguishes
      // "no signal" from "actively denied" so the backend doesn't record a
      // denial the user never made (createEmbeddedCheckoutSession drops
      // undefined fields). All three ad axes derive from the `advertisement`
      // category, matching the browser-event consent mapping.
      const currentConsent = new ConsentManager().getCurrentConsent();
      const adConsent: 'granted' | 'denied' | undefined = currentConsent
        ? currentConsent.advertisement
          ? 'granted'
          : 'denied'
        : undefined;
      const consent = adConsent
        ? {
            adStorage: adConsent,
            adUserData: adConsent,
            adPersonalization: adConsent
          }
        : undefined;

      // FFH-1533: the funnel paywall asks to return to its own welcome page.
      // The resolver only ever yields an allowlisted path, so a tampered
      // query param falls back to /thank-you.
      const returnPath = resolveCheckoutReturnPath(router.query.returnPath);

      const session = await createEmbeddedCheckoutSession({
        accessToken,
        planId: planIdNumber,
        coupon,
        referral: referralId,
        fbClickId,
        externalId: externalId?.toString(),
        email: user?.email,
        purchasePixelEventId,
        fbpCookie,
        utmFirstTouch,
        utmLastTouch,
        sourceUrl,
        firstLandingPage,
        consent,
        returnPath,
        trialPeriodDays
      });

      if ('error' in session) {
        // FFH-2592: the API refuses to create a session for a member who
        // already has a live subscription, so this is not a failure. It is
        // the guard working. Send them to the plan controls instead of the
        // generic error screen. Tracked as its own reason so the funnel does
        // not count a prevented duplicate as a broken checkout.
        // Status and code together: any other 409 is a real failure.
        if (
          session.failureStatus === HAS_LIVE_SUBSCRIPTION_STATUS &&
          session.failureCode === HAS_LIVE_SUBSCRIPTION_CODE
        ) {
          captureAnalyticsEvent(AnalyticsEvents.checkoutFailed, {
            reason: CHECKOUT_ERROR_REASON.alreadySubscribed,
            http_status: HAS_LIVE_SUBSCRIPTION_STATUS
          });
          router.replace(getExistingPlanPath(user, planIdNumber));
          return;
        }

        console.error('Stripe session error: failed to create session');
        datadogRumService.addError(
          new Error('Checkout session creation failed'),
          {
            reason: CHECKOUT_ERROR_REASON.sessionFailed,
            failureMode: 'api-response',
            endpoint: CHECKOUT_ENDPOINT,
            planId: parsePlanIdForTelemetry(planIdNumber)
          }
        );
        // FFH-2372: PostHog alongside RUM, so the funnel can subtract
        // failures from starts. The API surfaces only { error: true }, no
        // status; threading it out of the service is a follow-up.
        captureAnalyticsEvent(AnalyticsEvents.checkoutFailed, {
          reason: `${CHECKOUT_ERROR_REASON.sessionFailed}-api-response`,
          http_status: null
        });
        setIsError(true);
        return;
      }

      setClientSecret(session.clientSecret);
    } catch (error) {
      setIsError(true);
      console.error('Error retrieving Stripe client secret:', error);
      // "network" covers anything where no response arrived at all: CORS
      // blocks, offline, ad blockers. The error type disambiguates further.
      datadogRumService.addError(error, {
        reason: CHECKOUT_ERROR_REASON.sessionFailed,
        failureMode: 'network',
        endpoint: CHECKOUT_ENDPOINT,
        planId: parsePlanIdForTelemetry(planIdNumber)
      });
      // FFH-2372: nothing arrived at all (offline, CORS, ad blocker), so
      // there is no status to report.
      captureAnalyticsEvent(AnalyticsEvents.checkoutFailed, {
        reason: `${CHECKOUT_ERROR_REASON.sessionFailed}-network`,
        http_status: null
      });
    }
  };

  const isFetchingSession =
    !isError &&
    !clientSecret &&
    (accessTokenIsFetching || isLoading || Boolean(accessToken));

  const hasAuthSettled = !accessTokenIsFetching && !isLoading;
  const isMissingAccessToken = hasAuthSettled && !accessToken;
  const showSessionError = isError && hasAuthSettled && Boolean(accessToken);

  // Report "logged out on checkout" once per page view; it was previously
  // invisible - the visitor just got a generic error screen (FFH-1558).
  const hasReportedMissingTokenRef = useRef(false);
  useEffect(() => {
    if (!isMissingAccessToken || hasReportedMissingTokenRef.current) return;
    hasReportedMissingTokenRef.current = true;
    datadogRumService.addError(
      new Error('Checkout reached without an access token'),
      {
        reason: CHECKOUT_ERROR_REASON.missingAccessToken,
        planId: parsePlanIdForTelemetry(router.query.planId)
      }
    );
    // FFH-2372: the largest known drop-off class — logged out on arrival.
    captureAnalyticsEvent(AnalyticsEvents.checkoutFailed, {
      reason: CHECKOUT_ERROR_REASON.missingAccessToken,
      http_status: null
    });
  }, [isMissingAccessToken, router.query.planId]);

  return (
    <>
      {/* FFH-2380: the global chrome is hidden on this page (getInitialProps
          below) to keep the checkout distraction-free; FunnelHeader keeps the
          logo as a route back to the home page. */}
      <FunnelHeader />
      <div className="min-h-[600px] flex flex-col items-center px-4 py-8">
        <div className="w-full max-w-2xl">
          <h1 className="text-2xl font-bold text-center mb-2">
            Complete your sign up
          </h1>
          <p className="text-center text-neutral-600 mb-6">
            You&apos;re one step away from winning your mini-leagues
          </p>
          {isFetchingSession && (
            <div className="h-[300px] flex items-center justify-center">
              <AnimateSpinner customClasses="h-16 w-16" />
            </div>
          )}
          {isMissingAccessToken && (
            <div className="flex flex-col items-center justify-center my-12">
              <H2>Please log in to continue</H2>
              <p className="text-center mb-4">
                You need to be logged in to complete your purchase, or you can
                sign up from the join page.
              </p>
              <div className="flex flex-col space-y-4">
                <Button
                  variant="primary"
                  onClick={() => router.push(buildLoginHref(router.asPath))}
                >
                  Log in
                </Button>
                <Button variant="tertiary" onClick={() => router.push('/join')}>
                  Sign up
                </Button>
              </div>
            </div>
          )}
          {showSessionError && (
            <div className="flex flex-col items-center justify-center my-12">
              <H2>Something went wrong.</H2>
              <p className="text-center mb-4">
                We couldn&apos;t start the checkout. Please try again, or go
                back to the join page to select a membership.
              </p>
              <div className="flex flex-col space-y-4">
                <Button variant="primary" onClick={() => router.reload()}>
                  Try again
                </Button>
                <Button variant="tertiary" onClick={() => router.push('/join')}>
                  Back to join page
                </Button>
              </div>
            </div>
          )}
          {clientSecret && (
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ clientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>
      </div>
    </>
  );
};

// FFH-2380: hide the global header and footer; the FunnelHeader above keeps
// the logo as the route back to the home page.
CheckoutPage.getInitialProps = async () => ({ hideSiteChrome: true });

export default CheckoutPage;
